import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactECharts from 'echarts-for-react';
import DashboardCustomization from '../../components/dashboard/DashboardCustomization';
import { useAuth } from '../../contexts/AuthContext';
import projectService from '../../services/projectService';
import incidentService from '../../services/incidentService';
import inspectionService from '../../services/inspectionService';
import { CATEGORY_LABEL } from '../inspections/categoryOptions';
import '../../assets/styles/modern-dashboard.css';

// Maps each shortcuts section below to the Organisation.trackingCategories
// key that gates it. Sections with no entry here (Administration & Reports
// is meta, not a trackable category) always show.
const SECTION_CATEGORY_KEY = {
  'Environmental Monitoring': 'environmental',
  'Occupational Health & Safety (ILO Standards)': 'osh',
  'Labor & Working Conditions (ILO/ESS2)': 'labor',
  'Stakeholder Engagement & Grievances (ESS10)': 'stakeholder',
  'Gender-Based Violence & SEA Prevention': 'gbvPrevention',
  'Indigenous Peoples & Cultural Heritage (ESS7/ESS8)': 'culturalHeritage',
  'Permits, Licenses & Approvals': 'permits',
  'Road Safety': 'roadSafety',
};

// Severity-weighted compliance score, mirroring ComplianceService.calculateProjectFindingsScore's
// weighting client-side (same approach Reports.js already uses for its own score breakdown) -
// there's no dedicated backend endpoint for an org-wide/per-category score.
const FINDING_SEVERITY_WEIGHT = { critical: 5, major: 3, minor: 1 };
const FINDING_STATUS_PROGRESS = { not_done: 0, partially_done: 50, done: 100 };

const CATEGORY_KEYS = [
  'osh', 'environmental', 'labor', 'stakeholder', 'biodiversity',
  'culturalHeritage', 'gbvPrevention', 'childLabor', 'permits', 'roadSafety',
];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return startOfDay(d); };

function lastNMonths(n) {
  const months = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleString('default', { month: 'short' }).toUpperCase(),
    });
  }
  return months;
}

function monthKey(dateStr) {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export const DashboardTabs = () => {
  const [activeTab, setActiveTab] = useState('get-things-done');
  const [showCustomization, setShowCustomization] = useState(false);
  const navigate = useNavigate();
  const { organisation } = useAuth();

  // An org that hasn't onboarded yet (trackingCategories still null) keeps
  // seeing every section, matching pre-onboarding behaviour for existing
  // orgs; once set, only the selected categories' shortcuts show.
  const showSection = (sectionTitle) => {
    const key = SECTION_CATEGORY_KEY[sectionTitle];
    if (!key) return true;
    if (!organisation?.trackingCategories) return true;
    return !!organisation.trackingCategories[key];
  };
  const showEia = organisation?.usesEiaCertificates !== false;

  // Business Overview data - fetched lazily the first time that tab is
  // opened (mirrors Reports.js's org-wide data-fetch pattern) rather than
  // on every dashboard visit, since most sessions only use "Get things done".
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewLoaded, setOverviewLoaded] = useState(false);
  const [projects, setProjects] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [findings, setFindings] = useState([]);

  useEffect(() => {
    if (activeTab !== 'business-overview' || overviewLoaded) return;
    (async () => {
      try {
        setOverviewLoading(true);
        const [projectData, incidentData, inspectionData, findingData] = await Promise.all([
          projectService.getAll(),
          incidentService.getAll(),
          inspectionService.getAll(),
          inspectionService.getFindings(),
        ]);
        setProjects(projectData.projects || []);
        setIncidents(incidentData.incidents || []);
        setInspections(inspectionData.inspections || []);
        setFindings(findingData.findings || []);
        setOverviewLoaded(true);
      } catch (error) {
        console.error('Failed to load business overview data:', error);
        toast.error(error.response?.data?.error || 'Failed to load overview data');
      } finally {
        setOverviewLoading(false);
      }
    })();
  }, [activeTab, overviewLoaded]);

  // Severity-weighted compliance score for one category, blending findings
  // progress with the average completion% of inspections in that category -
  // null (rendered as "No data yet") when there's nothing to compute from.
  const categoryScore = (category) => {
    const catFindings = findings.filter((f) => f.category === category);
    const catInspections = inspections.filter((i) => i.category === category);
    if (catFindings.length === 0 && catInspections.length === 0) return null;

    let weighted = 0;
    let maxWeighted = 0;
    catFindings.forEach((f) => {
      const weight = FINDING_SEVERITY_WEIGHT[f.severity] || 1;
      weighted += weight * (FINDING_STATUS_PROGRESS[f.status] ?? 0);
      maxWeighted += weight * 100;
    });
    const findingsScore = maxWeighted > 0 ? weighted / maxWeighted : 1;

    const inspectionScore = catInspections.length
      ? catInspections.reduce((sum, i) => sum + (i.completionPercentage || 0), 0) / catInspections.length / 100
      : null;

    const score = inspectionScore === null ? findingsScore : (findingsScore + inspectionScore) / 2;
    return Math.round(score * 100);
  };

  // Default configuration
  const getDefaultConfig = () => ({
    shortcutCategories: [
      { id: 'project-management', title: 'Project Management', icon: 'fas fa-folder-plus' },
      { id: 'environmental-monitoring', title: 'Environmental Monitoring', icon: 'fas fa-leaf' },
      { id: 'osh', title: 'Occupational Health & Safety (ILO Standards)', icon: 'fas fa-hard-hat' },
      { id: 'labor', title: 'Labor & Working Conditions (ILO/ESS2)', icon: 'fas fa-users' },
      { id: 'stakeholder', title: 'Stakeholder Engagement & Grievances (ESS10)', icon: 'fas fa-handshake' },
      { id: 'gbv', title: 'Gender-Based Violence & SEA Prevention', icon: 'fas fa-shield-alt' },
      { id: 'indigenous', title: 'Indigenous Peoples & Cultural Heritage (ESS7/ESS8)', icon: 'fas fa-landmark' },
      { id: 'permits', title: 'Permits, Licenses & Approvals', icon: 'fas fa-file-signature' },
      { id: 'admin', title: 'Administration & Reports', icon: 'fas fa-chart-bar' },
    ],
    metricCards: [
      { id: 'osh-compliance', title: 'OSH Compliance (ILO)', icon: 'fas fa-shield-alt' },
      { id: 'environmental', title: 'Environmental (ESS)', icon: 'fas fa-leaf' },
      { id: 'labor-standards', title: 'Labor Standards (ILO)', icon: 'fas fa-users' },
      { id: 'stakeholder-engagement', title: 'Stakeholder Engagement (ESS10)', icon: 'fas fa-handshake' },
      { id: 'biodiversity', title: 'Biodiversity (ESS6)', icon: 'fas fa-tree' },
      { id: 'active-projects', title: 'Active Projects', icon: 'fas fa-project-diagram' },
      { id: 'gbv-prevention', title: 'GBV & SEA Prevention', icon: 'fas fa-shield-alt' },
      { id: 'indigenous-cultural', title: 'Indigenous & Cultural Heritage (ESS7/8)', icon: 'fas fa-landmark' },
      { id: 'grievances', title: 'Grievance Mechanism (ESS10)', icon: 'fas fa-inbox' },
      { id: 'working-conditions', title: 'Working Conditions (ILO/ESS2)', icon: 'fas fa-clock' },
      { id: 'child-labor', title: 'Child Labor Prevention (ILO)', icon: 'fas fa-child' },
      { id: 'permits-licenses', title: 'Permits & Licenses', icon: 'fas fa-file-signature' },
    ],
    selectedMetricCards: ['osh-compliance', 'environmental', 'labor-standards', 'stakeholder-engagement'],
  });

  // Load configuration from localStorage or use default
  const [dashboardConfig, setDashboardConfig] = useState(() => {
    const saved = localStorage.getItem('dashboardConfig');
    return saved ? JSON.parse(saved) : getDefaultConfig();
  });

  // Save configuration handler
  const handleSaveCustomization = (newConfig) => {
    setDashboardConfig(newConfig);
    localStorage.setItem('dashboardConfig', JSON.stringify(newConfig));
  };

  // A few stakeholder-engagement shortcuts still have no dedicated page -
  // show a lightweight notice instead of navigating to a dead link. (EIA
  // certificates/conditions now have real pages under /eia/certificates.)
  const showComingSoon = (feature) => {
    toast.info(`${feature} is coming soon!`);
  };

  // ---- Business Overview derived data (recomputed each render from the
  // fetched arrays above - cheap enough not to need memoization here) ----
  const activeProjectsCount = projects.filter((p) => p.status === 'active').length;
  const avgInspectionCompletion = inspections.length
    ? Math.round(inspections.reduce((sum, i) => sum + (i.completionPercentage || 0), 0) / inspections.length)
    : null;
  const openStakeholderFindings = findings.filter((f) => f.category === 'stakeholder' && f.status !== 'done').length;

  const inspectionsLast30 = inspections.filter((i) => i.submittedAt && new Date(i.submittedAt) >= daysAgo(30)).length;

  const incidentsLast30 = incidents.filter((i) => i.occurredAt && new Date(i.occurredAt) >= daysAgo(30));
  const majorIncidentsCount = incidentsLast30.filter((i) => ['major', 'fatal'].includes(i.severity)).length;
  const minorIncidentsCount = incidentsLast30.filter((i) => ['minor', 'moderate'].includes(i.severity)).length;
  const nearMissCount = incidentsLast30.filter((i) => i.severity === 'near_miss').length;

  const trackedCategoryKeys = CATEGORY_KEYS.filter((key) => (
    !organisation?.trackingCategories || organisation.trackingCategories[key]
  ));

  // Real "recent activity": merge the latest inspections/findings/incidents
  // by their actual date fields instead of a hardcoded list.
  const recentActivity = [
    ...inspections.map((i) => ({
      id: `inspection-${i.id}`,
      title: `${CATEGORY_LABEL[i.category] || i.category} inspection`,
      project: i.project?.name || 'Project',
      date: i.submittedAt,
      status: i.overallCompliance === 'non_compliant' ? 'non-compliant' : (i.overallCompliance === 'partial' ? 'needs-review' : 'compliant'),
    })),
    ...findings.map((f) => ({
      id: `finding-${f.id}`,
      title: f.title || 'Compliance finding',
      project: f.project?.name || 'Project',
      date: f.dateRaised,
      status: f.status === 'done' ? 'compliant' : 'non-compliant',
    })),
    ...incidents.map((i) => ({
      id: `incident-${i.id}`,
      title: i.title || 'Incident reported',
      project: i.project?.name || 'Project',
      date: i.occurredAt,
      status: ['fatal', 'major'].includes(i.severity) ? 'non-compliant' : 'needs-review',
    })),
  ]
    .filter((item) => !!item.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 8);

  // Compliance Trend - real last-6-months "Findings Raised vs Resolved",
  // replacing the previous hardcoded chart.
  const trendMonths = lastNMonths(6);
  const raisedByMonth = {};
  const resolvedByMonth = {};
  findings.forEach((f) => {
    if (f.dateRaised) {
      const k = monthKey(f.dateRaised);
      raisedByMonth[k] = (raisedByMonth[k] || 0) + 1;
    }
    if (f.dateClosed) {
      const k = monthKey(f.dateClosed);
      resolvedByMonth[k] = (resolvedByMonth[k] || 0) + 1;
    }
  });

  const complianceTrendOption = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#fff',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      textStyle: { color: '#374151' },
      axisPointer: { type: 'line', lineStyle: { color: '#d1d5db', width: 1 } }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '8%',
      top: '8%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: trendMonths.map((m) => m.label),
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { color: '#6b7280', fontSize: 11 }
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: '#f3f4f6' } },
      axisLabel: { color: '#6b7280', fontSize: 11 }
    },
    series: [
      {
        name: 'Findings Raised',
        type: 'bar',
        data: trendMonths.map((m) => raisedByMonth[m.key] || 0),
        itemStyle: {
          color: '#0891b2',
          borderRadius: [4, 4, 0, 0]
        },
        barWidth: '60%'
      },
      {
        name: 'Findings Resolved',
        type: 'bar',
        data: trendMonths.map((m) => resolvedByMonth[m.key] || 0),
        itemStyle: {
          color: '#16a34a',
          borderRadius: [4, 4, 0, 0]
        },
        barWidth: '60%'
      }
    ]
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'compliant':
        return <span className="status-badge status-success">Compliant</span>;
      case 'non-compliant':
        return <span className="status-badge status-danger">Non-Compliant</span>;
      case 'needs-review':
        return <span className="status-badge status-warning">Needs Review</span>;
      default:
        return <span className="status-badge status-secondary">Unknown</span>;
    }
  };

  return (
    <div className="modern-dashboard">
      {/* Tab Navigation */}
      <div className="tab-navigation">
        <div className="tab-buttons-group">
          <button 
            className={`tab-button ${activeTab === 'get-things-done' ? 'active' : ''}`}
            onClick={() => setActiveTab('get-things-done')}
          >
            Get things done
          </button>
          <button
            className={`tab-button ${activeTab === 'business-overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('business-overview')}
          >
            Business overview
          </button>
        </div>
        <button 
          className="customize-btn" 
          onClick={() => setShowCustomization(true)}
          data-tooltip="Customize Dashboard"
        >
          <i className="fas fa-cog"></i>
        </button>
      </div>

      {/* Get Things Done Tab */}
      {activeTab === 'get-things-done' && (
        <div className="tab-content-wrapper">
          {/* Shortcuts */}
          <div className="shortcuts-section">
            <h2 className="section-title">Shortcuts</h2>
            
            {/* Project Management */}
            <div className="task-category">
              <h3 className="category-title">Project Management</h3>
              <div className="shortcuts-grid">
                <button 
                  className="shortcut-card"
                  onClick={() => navigate('/projects', { state: { openCreateModal: true } })}
                >
                  <div className="shortcut-icon">
                    <i className="fas fa-folder-plus"></i>
                  </div>
                  <span className="shortcut-label">Create project</span>
                </button>
                
                <Link to="/projects" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-project-diagram"></i>
                  </div>
                  <span className="shortcut-label">View all projects</span>
                </Link>
                
                {showEia && (
                  <>
                    <Link to="/eia/certificates" className="shortcut-card">
                      <div className="shortcut-icon">
                        <i className="fas fa-file-contract"></i>
                      </div>
                      <span className="shortcut-label">Register EIA</span>
                    </Link>

                    {/* Conditions are added from within a certificate (they belong
                        to one), so this shortcut lands on the certificates list
                        first - open one (or register a new one) to add a condition. */}
                    <Link to="/eia/certificates" className="shortcut-card">
                      <div className="shortcut-icon">
                        <i className="fas fa-list-check"></i>
                      </div>
                      <span className="shortcut-label">Add EIA condition</span>
                    </Link>

                    <Link to="/eia/certificates" className="shortcut-card">
                      <div className="shortcut-icon">
                        <i className="fas fa-certificate"></i>
                      </div>
                      <span className="shortcut-label">View certificates</span>
                    </Link>

                    <Link to="/eia/certificates" className="shortcut-card">
                      <div className="shortcut-icon">
                        <i className="fas fa-clipboard-list"></i>
                      </div>
                      <span className="shortcut-label">Manage conditions</span>
                    </Link>
                  </>
                )}

                <Link to="/monitoring/records/new" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-file-alt"></i>
                  </div>
                  <span className="shortcut-label">Submit record</span>
                </Link>

                <Link to="/monitoring/tasks" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-tasks"></i>
                  </div>
                  <span className="shortcut-label">View monitoring tasks</span>
                </Link>
                
                <Link to="/monitoring/records" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-history"></i>
                  </div>
                  <span className="shortcut-label">View records</span>
                </Link>
                
                <button className="shortcut-card" onClick={() => showComingSoon('Compliance summary')}>
                  <div className="shortcut-icon">
                    <i className="fas fa-chart-pie"></i>
                  </div>
                  <span className="shortcut-label">Compliance summary</span>
                </button>
              </div>
            </div>

            {/* Environmental Monitoring - every original shortcut restored, each
                pointing at the reusable Category page for that specific topic.
                That page shows real configured data when it exists, or a clear
                "not configured yet" state when it doesn't - never a blank page. */}
            {showSection('Environmental Monitoring') && (
            <div className="task-category">
              <h3 className="category-title">Environmental Monitoring</h3>
              <div className="shortcuts-grid">
                <Link to="/category/habitatAssessment" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-tree"></i>
                  </div>
                  <span className="shortcut-label">Habitat assessment</span>
                </Link>

                <Link to="/category/speciesMonitoring" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-paw"></i>
                  </div>
                  <span className="shortcut-label">Species monitoring</span>
                </Link>

                <Link to="/category/invasiveSpecies" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-bug"></i>
                  </div>
                  <span className="shortcut-label">Invasive species</span>
                </Link>

                <Link to="/category/biodiversityOffset" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-seedling"></i>
                  </div>
                  <span className="shortcut-label">Biodiversity offset</span>
                </Link>

                <Link to="/category/airQuality" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-wind"></i>
                  </div>
                  <span className="shortcut-label">Air quality</span>
                </Link>

                <Link to="/category/waterQuality" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-tint"></i>
                  </div>
                  <span className="shortcut-label">Water monitoring</span>
                </Link>

                <Link to="/category/noiseMonitoring" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-volume-up"></i>
                  </div>
                  <span className="shortcut-label">Noise monitoring</span>
                </Link>

                <Link to="/category/wasteManagement" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-recycle"></i>
                  </div>
                  <span className="shortcut-label">Waste management</span>
                </Link>

                <Link to="/category/socialComponent" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-users"></i>
                  </div>
                  <span className="shortcut-label">Social component</span>
                </Link>

                <Link to="/projects" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-list"></i>
                  </div>
                  <span className="shortcut-label">View all components</span>
                </Link>
              </div>
            </div>
            )}

            {/* Occupational Health & Safety (ILO Standards) - Incidents/Inspections stay
                as their own real pages; every other original shortcut is restored and
                routed through the Category page. */}
            {showSection('Occupational Health & Safety (ILO Standards)') && (
            <div className="task-category">
              <h3 className="category-title">Occupational Health & Safety (ILO Standards)</h3>
              <div className="shortcuts-grid">
                <Link to="/inspections" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-clipboard-check"></i>
                  </div>
                  <span className="shortcut-label">Conduct inspection</span>
                </Link>

                <Link to="/inspections" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-list-alt"></i>
                  </div>
                  <span className="shortcut-label">View inspections</span>
                </Link>

                <Link to="/incidents" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                  <span className="shortcut-label">Report incident</span>
                </Link>

                <Link to="/incidents" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-bell"></i>
                  </div>
                  <span className="shortcut-label">View incidents</span>
                </Link>

                <Link to="/risk-assessment" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-exclamation-circle"></i>
                  </div>
                  <span className="shortcut-label">Risk assessment</span>
                </Link>

                <Link to="/category/ppeManagement" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-hard-hat"></i>
                  </div>
                  <span className="shortcut-label">PPE management</span>
                </Link>

                <Link to="/category/safetyTraining" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-graduation-cap"></i>
                  </div>
                  <span className="shortcut-label">Safety training</span>
                </Link>

                <Link to="/category/medicalSurveillance" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-heartbeat"></i>
                  </div>
                  <span className="shortcut-label">Medical surveillance</span>
                </Link>

                <Link to="/category/emergencyResponse" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-ambulance"></i>
                  </div>
                  <span className="shortcut-label">Emergency response</span>
                </Link>

                <Link to="/category/workplaceInspections" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-search"></i>
                  </div>
                  <span className="shortcut-label">Workplace inspections</span>
                </Link>
              </div>
            </div>
            )}

            {/* Labor & Working Conditions (ILO/ESS2) */}
            {showSection('Labor & Working Conditions (ILO/ESS2)') && (
            <div className="task-category">
              <h3 className="category-title">Labor & Working Conditions (ILO/ESS2)</h3>
              <div className="shortcuts-grid">
                <Link to="/category/workingHours" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-clock"></i>
                  </div>
                  <span className="shortcut-label">Working hours</span>
                </Link>

                <Link to="/category/wagesBenefits" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-money-bill-wave"></i>
                  </div>
                  <span className="shortcut-label">Wages & benefits</span>
                </Link>

                <Link to="/category/childLabor" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-child"></i>
                  </div>
                  <span className="shortcut-label">Child labor check</span>
                </Link>

                <Link to="/category/nonDiscrimination" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-balance-scale"></i>
                  </div>
                  <span className="shortcut-label">Non-discrimination</span>
                </Link>

                <Link to="/category/workersOrganization" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-handshake"></i>
                  </div>
                  <span className="shortcut-label">Workers organization</span>
                </Link>

                <Link to="/category/contractedWorkers" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-hard-hat"></i>
                  </div>
                  <span className="shortcut-label">Contracted workers</span>
                </Link>
              </div>
            </div>
            )}

            {/* Stakeholder Engagement & Grievances (ESS10) */}
            {showSection('Stakeholder Engagement & Grievances (ESS10)') && (
            <div className="task-category">
              <h3 className="category-title">Stakeholder Engagement & Grievances (ESS10)</h3>
              <div className="shortcuts-grid">
                <Link to="/forms?category=stakeholder" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-users"></i>
                  </div>
                  <span className="shortcut-label">Stakeholder register</span>
                </Link>

                <Link to="/forms?category=stakeholder" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-comments"></i>
                  </div>
                  <span className="shortcut-label">Public consultation</span>
                </Link>

                <Link to="/forms?category=stakeholder" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-bullhorn"></i>
                  </div>
                  <span className="shortcut-label">Log grievance</span>
                </Link>

                <Link to="/forms?category=stakeholder" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-inbox"></i>
                  </div>
                  <span className="shortcut-label">Grievance tracker</span>
                </Link>
              </div>
            </div>
            )}

            {/* Gender-Based Violence & SEA Prevention */}
            {showSection('Gender-Based Violence & SEA Prevention') && (
            <div className="task-category">
              <h3 className="category-title">Gender-Based Violence & SEA Prevention</h3>
              <div className="shortcuts-grid">
                <Link to="/forms?category=gbvPrevention" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <span className="shortcut-label">GBV tracker</span>
                </Link>

                <Link to="/category/gbvTraining" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-chalkboard-teacher"></i>
                  </div>
                  <span className="shortcut-label">GBV awareness training</span>
                </Link>

                <Link to="/category/codeOfConduct" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-file-signature"></i>
                  </div>
                  <span className="shortcut-label">Code of conduct</span>
                </Link>

                <Link to="/category/gbvResponse" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-hands-helping"></i>
                  </div>
                  <span className="shortcut-label">Response services</span>
                </Link>
              </div>
            </div>
            )}

            {/* Indigenous Peoples & Cultural Heritage (ESS7/ESS8) */}
            {showSection('Indigenous Peoples & Cultural Heritage (ESS7/ESS8)') && (
            <div className="task-category">
              <h3 className="category-title">Indigenous Peoples & Cultural Heritage (ESS7/ESS8)</h3>
              <div className="shortcuts-grid">
                <Link to="/category/indigenousPeoples" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-comments"></i>
                  </div>
                  <span className="shortcut-label">FPIC process</span>
                </Link>

                <Link to="/category/indigenousLands" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-map-marked"></i>
                  </div>
                  <span className="shortcut-label">Indigenous lands</span>
                </Link>

                <Link to="/category/culturalHeritage" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-landmark"></i>
                  </div>
                  <span className="shortcut-label">Cultural heritage</span>
                </Link>

                <Link to="/category/chanceFinds" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-scroll"></i>
                  </div>
                  <span className="shortcut-label">Chance finds protocol</span>
                </Link>
              </div>
            </div>
            )}

            {/* Permits, Licenses & Approvals */}
            {showSection('Permits, Licenses & Approvals') && (
            <div className="task-category">
              <h3 className="category-title">Permits, Licenses & Approvals</h3>
              <div className="shortcuts-grid">
                <Link to="/category/permits" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-leaf"></i>
                  </div>
                  <span className="shortcut-label">Environmental permits</span>
                </Link>

                <Link to="/category/operationalLicenses" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-industry"></i>
                  </div>
                  <span className="shortcut-label">Operational licenses</span>
                </Link>

                <Link to="/category/waterPermits" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-tint"></i>
                  </div>
                  <span className="shortcut-label">Water use permits</span>
                </Link>

                <Link to="/category/wastePermits" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-trash"></i>
                  </div>
                  <span className="shortcut-label">Waste disposal permits</span>
                </Link>
              </div>
            </div>
            )}

            {/* Road Safety */}
            {showSection('Road Safety') && (
            <div className="task-category">
              <h3 className="category-title">Road Safety</h3>
              <div className="shortcuts-grid">
                <Link to="/category/vehicleInspections" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-truck"></i>
                  </div>
                  <span className="shortcut-label">Vehicle inspections</span>
                </Link>

                <Link to="/category/driverSafetyTraining" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-id-card"></i>
                  </div>
                  <span className="shortcut-label">Driver safety training</span>
                </Link>

                <Link to="/category/speedMonitoring" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-tachometer-alt"></i>
                  </div>
                  <span className="shortcut-label">Speed monitoring</span>
                </Link>

                <Link to="/category/journeyManagement" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-route"></i>
                  </div>
                  <span className="shortcut-label">Journey management</span>
                </Link>

                <Link to="/category/roadIncidents" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-car-crash"></i>
                  </div>
                  <span className="shortcut-label">Road incident tracking</span>
                </Link>

                <Link to="/forms?category=roadSafety" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-file-signature"></i>
                  </div>
                  <span className="shortcut-label">Community safety reports</span>
                </Link>
              </div>
            </div>
            )}

            {/* Administration & Reports */}
            <div className="task-category">
              <h3 className="category-title">Administration & Reports</h3>
              <div className="shortcuts-grid">
                <Link to="/reports" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-file-pdf"></i>
                  </div>
                  <span className="shortcut-label">Compliance report</span>
                </Link>

                <Link to="/reports" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <span className="shortcut-label">Environmental report</span>
                </Link>

                <Link to="/team" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-users"></i>
                  </div>
                  <span className="shortcut-label">Manage team</span>
                </Link>

                <Link to="/settings" className="shortcut-card">
                  <div className="shortcut-icon">
                    <i className="fas fa-cog"></i>
                  </div>
                  <span className="shortcut-label">Settings</span>
                </Link>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Business Overview Tab - real data, fetched lazily above the first
          time this tab is activated. */}
      {activeTab === 'business-overview' && (
        <div className="tab-content-wrapper overview-tab">
          {overviewLoading && !overviewLoaded && (
            <p style={{ color: '#6b7280', marginBottom: '16px' }}>
              <i className="fas fa-spinner fa-spin"></i> Loading real-time data...
            </p>
          )}
          {/* Key Metrics Banner */}
          <div className="metrics-banner">
            {dashboardConfig.selectedMetricCards?.map((cardId) => {
              const renderScoreCard = (label, icon, cardClass, category) => {
                const score = categoryScore(category);
                return (
                  <div key={cardId} className={`metric-card ${cardClass}`}>
                    <div className="metric-icon"><i className={icon}></i></div>
                    <div className="metric-content">
                      <div className="metric-label">{label}</div>
                      <div className="metric-value">{score === null ? '—' : `${score}%`}</div>
                      <div className="metric-trend neutral">
                        <i className={score === null ? 'fas fa-info-circle' : 'fas fa-chart-line'}></i>
                        {score === null ? ' No data yet' : ' Findings + inspections'}
                      </div>
                    </div>
                  </div>
                );
              };

              // Render metric card based on ID
              switch(cardId) {
                case 'osh-compliance':
                  return renderScoreCard('OSH Compliance (ILO)', 'fas fa-shield-alt', 'success', 'osh');

                case 'environmental':
                  return renderScoreCard('Environmental (ESS)', 'fas fa-leaf', 'primary', 'environmental');

                case 'labor-standards':
                  return renderScoreCard('Labor Standards (ILO)', 'fas fa-users', 'warning', 'labor');

                case 'stakeholder-engagement':
                  return renderScoreCard('Stakeholder Engagement', 'fas fa-handshake', 'info', 'stakeholder');

                case 'biodiversity':
                  return renderScoreCard('Biodiversity (ESS6)', 'fas fa-tree', 'success', 'biodiversity');

                case 'active-projects':
                  return (
                    <div key={cardId} className="metric-card primary">
                      <div className="metric-icon">
                        <i className="fas fa-project-diagram"></i>
                      </div>
                      <div className="metric-content">
                        <div className="metric-label">Active Projects</div>
                        <div className="metric-value">{activeProjectsCount}</div>
                        <div className="metric-trend neutral">
                          <i className="fas fa-folder"></i> Of {projects.length} total
                        </div>
                      </div>
                    </div>
                  );

                case 'gbv-prevention':
                  return renderScoreCard('GBV & SEA Prevention', 'fas fa-shield-alt', 'warning', 'gbvPrevention');

                case 'indigenous-cultural':
                  return renderScoreCard('Indigenous & Cultural (ESS7/8)', 'fas fa-landmark', 'info', 'culturalHeritage');

                case 'grievances':
                  return (
                    <div key={cardId} className="metric-card warning">
                      <div className="metric-icon">
                        <i className="fas fa-inbox"></i>
                      </div>
                      <div className="metric-content">
                        <div className="metric-label">Grievances (ESS10)</div>
                        <div className="metric-value">{openStakeholderFindings}</div>
                        <div className="metric-trend neutral">
                          <i className="fas fa-inbox"></i> Open cases
                        </div>
                      </div>
                    </div>
                  );

                case 'working-conditions':
                  return (
                    <div key={cardId} className="metric-card success">
                      <div className="metric-icon">
                        <i className="fas fa-clock"></i>
                      </div>
                      <div className="metric-content">
                        <div className="metric-label">Working Conditions (ILO/ESS2)</div>
                        <div className="metric-value">{avgInspectionCompletion === null ? '—' : `${avgInspectionCompletion}%`}</div>
                        <div className="metric-trend neutral">
                          <i className="fas fa-clipboard-check"></i> Avg. inspection completion
                        </div>
                      </div>
                    </div>
                  );

                case 'child-labor':
                  return renderScoreCard('Child Labor Prevention (ILO)', 'fas fa-child', 'success', 'childLabor');

                case 'permits-licenses':
                  return renderScoreCard('Permits & Licenses', 'fas fa-file-signature', 'primary', 'permits');

                default:
                  return null;
              }
            })}
          </div>

          {/* Main Content Grid */}
          <div className="overview-grid">
            {/* Compliance Tracking Chart */}
            <div className="overview-card chart-card">
              <div className="card-header-modern">
                <div>
                  <h3 className="card-title-modern">COMPLIANCE TRACKING</h3>
                  <p className="card-subtitle">Monthly compliance vs non-compliance trends</p>
                </div>
                <div className="legend-inline">
                  <span className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#0891b2' }}></span>
                    Findings Raised
                  </span>
                  <span className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#16a34a' }}></span>
                    Findings Resolved
                  </span>
                </div>
              </div>
              <ReactECharts option={complianceTrendOption} style={{ height: '320px' }} />
              <div className="card-footer-modern">
                <Link to="/reports" className="view-details-link">
                  View detailed compliance report <i className="fas fa-arrow-right"></i>
                </Link>
              </div>
            </div>

            {/* Inspections Overview */}
            <div className="overview-card">
              <div className="card-header-modern">
                <h3 className="card-title-modern">INSPECTIONS</h3>
                <Link to="/inspections" className="card-action-link">View all</Link>
              </div>
              <div className="stats-list">
                <div className="stat-row">
                  <div className="stat-info">
                    <div className="stat-icon completed">
                      <i className="fas fa-check"></i>
                    </div>
                    <div>
                      <div className="stat-label">Completed</div>
                      <div className="stat-sublabel">Last 30 days</div>
                    </div>
                  </div>
                  <div className="stat-number">{inspectionsLast30}</div>
                </div>
                <div className="stat-row">
                  <div className="stat-info">
                    <div className="stat-icon pending">
                      <i className="fas fa-clipboard-list"></i>
                    </div>
                    <div>
                      <div className="stat-label">Total Submitted</div>
                      <div className="stat-sublabel">All time</div>
                    </div>
                  </div>
                  <div className="stat-number">{inspections.length}</div>
                </div>
                <div className="stat-row">
                  <div className="stat-info">
                    <div className="stat-icon" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
                      <i className="fas fa-percent"></i>
                    </div>
                    <div>
                      <div className="stat-label">Avg. Completion</div>
                      <div className="stat-sublabel">All submitted inspections</div>
                    </div>
                  </div>
                  <div className="stat-number">{avgInspectionCompletion === null ? '—' : `${avgInspectionCompletion}%`}</div>
                </div>
              </div>
            </div>

            {/* Incidents Overview */}
            <div className="overview-card">
              <div className="card-header-modern">
                <h3 className="card-title-modern">INCIDENTS</h3>
                <Link to="/incidents" className="card-action-link">View all</Link>
              </div>
              <div className="stats-list">
                <div className="stat-row">
                  <div className="stat-info">
                    <div className="stat-icon" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
                      <i className="fas fa-info-circle"></i>
                    </div>
                    <div>
                      <div className="stat-label">Minor incidents</div>
                      <div className="stat-sublabel">Last 30 days</div>
                    </div>
                  </div>
                  <div className="stat-number">{minorIncidentsCount}</div>
                </div>
                <div className="stat-row">
                  <div className="stat-info">
                    <div className="stat-icon" style={{ backgroundColor: '#fef3c7', color: '#b45309' }}>
                      <i className="fas fa-exclamation-triangle"></i>
                    </div>
                    <div>
                      <div className="stat-label">Major incidents</div>
                      <div className="stat-sublabel">Last 30 days</div>
                    </div>
                  </div>
                  <div className="stat-number">{majorIncidentsCount}</div>
                </div>
                <div className="stat-row">
                  <div className="stat-info">
                    <div className="stat-icon" style={{ backgroundColor: '#dcfce7', color: '#15803d' }}>
                      <i className="fas fa-file-alt"></i>
                    </div>
                    <div>
                      <div className="stat-label">Near misses</div>
                      <div className="stat-sublabel">Last 30 days</div>
                    </div>
                  </div>
                  <div className="stat-number">{nearMissCount}</div>
                </div>
              </div>
            </div>

            {/* One reusable card per org-tracked category, replacing the previous
                bespoke Labor/Grievances/GBV/Indigenous/Biodiversity cards - all of
                those had no real backing data source beyond findings/inspections
                filed under that category, so this uses the same real numbers for
                every category instead of inventing category-specific stats. */}
            {trackedCategoryKeys.map((key) => {
              const catFindings = findings.filter((f) => f.category === key);
              const openCount = catFindings.filter((f) => f.status !== 'done').length;
              const closedCount = catFindings.filter((f) => f.status === 'done').length;
              const catInspectionsCount = inspections.filter((i) => i.category === key).length;
              const score = categoryScore(key);
              return (
                <div key={key} className="overview-card">
                  <div className="card-header-modern">
                    <h3 className="card-title-modern">{(CATEGORY_LABEL[key] || key).toUpperCase()}</h3>
                    <Link to={`/category/${key}`} className="card-action-link">View details</Link>
                  </div>
                  <div className="stats-list">
                    <div className="stat-row">
                      <div className="stat-info">
                        <div className="stat-icon overdue">
                          <i className="fas fa-exclamation-circle"></i>
                        </div>
                        <div>
                          <div className="stat-label">Open Findings</div>
                          <div className="stat-sublabel">Needs action</div>
                        </div>
                      </div>
                      <div className="stat-number">{openCount}</div>
                    </div>
                    <div className="stat-row">
                      <div className="stat-info">
                        <div className="stat-icon completed">
                          <i className="fas fa-check-circle"></i>
                        </div>
                        <div>
                          <div className="stat-label">Closed Findings</div>
                          <div className="stat-sublabel">Resolved</div>
                        </div>
                      </div>
                      <div className="stat-number">{closedCount}</div>
                    </div>
                    <div className="stat-row">
                      <div className="stat-info">
                        <div className="stat-icon" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
                          <i className="fas fa-clipboard-check"></i>
                        </div>
                        <div>
                          <div className="stat-label">Inspections Submitted</div>
                          <div className="stat-sublabel">All time</div>
                        </div>
                      </div>
                      <div className="stat-number">{catInspectionsCount}</div>
                    </div>
                    <div className="stat-row">
                      <div className="stat-info">
                        <div className="stat-icon" style={{ backgroundColor: '#fef3c7', color: '#b45309' }}>
                          <i className="fas fa-percent"></i>
                        </div>
                        <div>
                          <div className="stat-label">Compliance Score</div>
                          <div className="stat-sublabel">Findings + inspections</div>
                        </div>
                      </div>
                      <div className="stat-number">{score === null ? '—' : `${score}%`}</div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Recent Activity */}
            <div className="overview-card activity-card">
              <div className="card-header-modern">
                <h3 className="card-title-modern">RECENT ACTIVITY</h3>
              </div>
              <div className="activity-list">
                {recentActivity.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>No activity yet.</p>
                ) : recentActivity.map((activity) => (
                  <div key={activity.id} className="activity-item">
                    <div className="activity-icon">
                      <i className="fas fa-clipboard-check"></i>
                    </div>
                    <div className="activity-content">
                      <div className="activity-title">{activity.title}</div>
                      <div className="activity-meta">
                        <span>{activity.project}</span>
                        <span className="separator">•</span>
                        <span>{new Date(activity.date).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div>{getStatusBadge(activity.status)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customization Modal */}
      <DashboardCustomization
        isOpen={showCustomization}
        onClose={() => setShowCustomization(false)}
        onSave={handleSaveCustomization}
        initialConfig={{
          ...dashboardConfig,
          defaultShortcutCategories: getDefaultConfig().shortcutCategories,
          defaultMetricCards: getDefaultConfig().metricCards,
          defaultSelectedMetricCards: getDefaultConfig().selectedMetricCards,
        }}
      />
    </div>
  );
};
